import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { categoryKeys, jobTypeKeys } from "@/lib/query";
import type { CreateJobTypeInput, UpdateJobTypeInput } from "@/schemas/categories";

export interface JobType {
  id: string;
  code: string;
  label: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export function useJobTypes() {
  return useQuery({
    queryKey: jobTypeKeys.list(),
    queryFn: async (): Promise<JobType[]> => {
      const res = await fetch("/api/job-types");
      if (!res.ok) throw new Error("Failed to fetch job types");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateJobType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateJobTypeInput) => {
      const res = await fetch("/api/job-types", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error((data as { error?: string }).error ?? "Failed to create job type");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
      toast.success("Job type created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateJobType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateJobTypeInput) => {
      const res = await fetch(`/api/job-types/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to update job type");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      toast.success("Job type updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteJobType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/job-types/${id}`, { method: "DELETE" });
      if (res.status === 409) {
        const data = await res.json();
        throw new Error(
          `Cannot delete — ${(data as { count: number }).count} category tag(s) reference this job type`,
        );
      }
      if (!res.ok) throw new Error("Failed to delete job type");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
      toast.success("Job type deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useReorderJobTypes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const res = await fetch("/api/job-types/reorder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedIds }),
      });
      if (!res.ok) throw new Error("Failed to reorder job types");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobTypeKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
